import React, { Fragment, ReactElement, ReactNode } from "react";
import { isFragment } from "react-is";
import styled from "styled-components";

import ContainerButton from "../buttons/ContainerButton";
import Truncate from "../utils/Truncate";

interface StyledTabsProps {
  stretch?: boolean;
  padding?: string;
}

/**
 * Highest level container for Tabs; contains TabItems and TabItem components.
 */
const StyledTabs = styled.div.attrs({
  role: "tablist",
})<{ padding?: string }>`
  border-bottom: solid 2px #ddd;

  ${(props) => {
    if (props.padding) {
      return `
        padding-left: ${props.padding};
        padding-top: ${props.padding};  
        padding-right: ${props.padding};  
      `;
    }
  }}
`;

/**
 * Controls spacing between TabItem components and drops TabItems down using
 * negative margin. */
const StyledTabItems = styled.div<StyledTabsProps>`
  display: flex;
  gap: 0.1rem;
  margin-bottom: -2px;

  & > div {
    min-width: 0; /* truncate helper */
  }

  ${(props) => {
    if (props.stretch) {
      return `
        width: 100%;
        
        & > button {
          flex: 1;
          text-align: center;
          
        }
      `;
    }
  }}
`;

export type Props = StyledTabsProps & {
  value?: string;
  onChange?: (value: string) => void;
};

/** Iterator component that applies props and events to the `TabItem` children */
export const TabsBase: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const renderClone = (tab: ReactElement, idx: number) => {
    return React.cloneElement(tab, {
      key: idx,
      selected: props.value === tab.props.value ? true : false,
      ...(props.value === tab.props.value ? { "aria-selected": true } : {}),
      role: "tab",
      onClick: () => {
        props.onChange?.(tab.props.value);
      },
      ...tab.props,
    });
  };

  const renderChildren = (children: ReactNode): ReactElement => {
    return React.Children.map(children, (tab: any, idx) => {
      if (!React.isValidElement<any>(tab)) return null;
      if (isFragment(tab)) {
        return renderChildren(tab.props.children);
      }
      return renderClone(tab, idx);
    });
  };

  return <>{renderChildren(props.children)}</>;
};

const Tabs: React.FC<React.PropsWithChildren<Props>> & {
  TabItem: typeof TabItem;
} = (props) => {
  return (
    <StyledTabs padding={props.padding}>
      <StyledTabItems stretch={props.stretch}>
        <TabsBase {...props}>{props.children}</TabsBase>
      </StyledTabItems>
    </StyledTabs>
  );
};

export interface TabItemProps {
  value: string;
  selected?: boolean;
  count?: number;
  title?: string;
  noTruncate?: boolean;
}

const StyledTab = styled(ContainerButton).attrs<Partial<TabItemProps>>(
  (props) => ({
    role: "tab",
    "aria-selected": props.selected,
    tabindex: props.selected ? 0 : -1,
  }),
)<Partial<TabItemProps>>`
  color: hsl(0, 0%, 30%);
  height: 3rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  border-bottom: 2px solid #ddd;
  transition: border 0.2s;
  padding: 0 1rem;

  &:hover {
    border-bottom: 2px solid #aaa;
  }

  ${({ selected }) =>
    selected &&
    `
      color: hsl(0, 0%, 30%);
      border-bottom: 2px solid rgb(212, 24, 24);
      font-weight: 700;

      &:hover, &:focus-visible {
        border-bottom: 2px solid rgb(212, 24, 24);
      }
    `};
`;

const Count = styled.div`
  background-color: #006699;
  color: #fff;
  border-radius: 0.3rem;
  padding: 0.2rem 0.6rem;
  font-weight: normal;
`;

export const TabItem: React.FC<React.PropsWithChildren<TabItemProps>> = (
  props,
) => {
  return (
    <StyledTab {...props}>
      {!props.noTruncate ? (
        <Truncate>{props.children}</Truncate>
      ) : (
        <>{props.children}</>
      )}
      {props.count !== undefined && <Count>{props.count}</Count>}
    </StyledTab>
  );
};

Tabs.TabItem = TabItem;
export default Tabs;

/**
 * BetterTabs
 *
 * This component is an evolution of the Tabs component. It type checks your tab names
 * and avoids using any React.Children.map -> React.cloneElement kerfuffle.
 * It also allows you to hide tabs by setting the hidden prop to true.
 *
 *  Usage:
 *  ```tsx
 *  const [tab, setTab] = useState<"academic" | "course">("academic");
 *
 *  <BetterTabs
 *    activeTab={activeTab}
 *    onTabChange={(tab) => setTab(tab)}
 *    tabs={[
 *     {
 *       tab: "academic",
 *       label: "Academic Calendars",
 *       element: <ManageAcademicCalendars />,
 *     },
 *     {
 *       ...
 *     }
 *    ]}
 *  />
 */

type BetterTabProps<Tabs extends string | undefined> = {
  activeTab: Tabs;
  onTabChange: (tab: Tabs) => void;
  tabs: Array<{
    tab: Tabs;
    label: string;
    element: React.ReactNode;
    hidden?: boolean;
  }>;
};
export const BetterTabs = <Tabs extends string | undefined>(
  props: BetterTabProps<Tabs>,
) => (
  <>
    <StyledTabs>
      <StyledTabItems>
        {props.tabs
          .filter(({ hidden }) => !hidden)
          .map(({ tab, label }) => (
            <StyledTab
              key={tab ?? "default"}
              value={tab}
              selected={props.activeTab === tab}
              onClick={() => props.onTabChange(tab)}
            >
              {label}
            </StyledTab>
          ))}
      </StyledTabItems>
    </StyledTabs>
    {props.tabs.find(({ tab }) => tab === props.activeTab)?.element}
  </>
);
